import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  ViewEncapsulation,
  input,
  effect,
  signal,
  viewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import videojs from 'video.js';
import 'videojs-contrib-ads';
import 'videojs-ima';
import { EnvironmentService } from '../../../environments/environment.service';
import { LogService } from '../../core/log/log.service';
import { TargetBanner } from '../banner/banner.model';
import { MatIconModule } from '@angular/material/icon';
import { Store } from '@ngrx/store';
import { selectAuthLoginState } from '../../core/auth/auth.selectors';
import { AuthLoginState } from '../../core/auth/auth.models';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'dottnet-video-ads',
  templateUrl: 'video-ads.component.html',
  styleUrls: ['video-ads.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [MatIconModule],
  standalone: true
})
export class VideoAdsComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly divVideoPlayerRef = viewChild<ElementRef>('divVideoContainer');

  // Signal inputs replacing Observable inputs
  readonly targetBanner = input<TargetBanner | null>(null);
  readonly reloadAds = input<boolean>(false);

  // Video player
  player: any;
  isShow = signal(true);
  adTagUrl: string;
  cat: string;
  pr: string;
  spec: string;
  cont: string;
  tcont: string;
  can: string;
  tcan: string;
  videoContent: string;

  // Track login state
  loginState: AuthLoginState | null = null;

  constructor(
    private logService: LogService,
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object,
    private envService: EnvironmentService,
    private store: Store,
  ) {
    // Setup effects to react to signal changes
    effect(() => {
      const target = this.targetBanner();
      if (target) {
        this.logService.info('VideoADS: effect targetBanner', target);
        this.handleTargetBannerChange(target);
      }
    });

    effect(() => {
      const reload = this.reloadAds();
      if (reload && this.player && 'ima' in this.player) {
        this.logService.info('VideoADS: effect reload adTagUrl', this.adTagUrl);
        this.reloadPlayer();
      }
    });
  }

  ngOnInit() {
    this.videoContent =
      '<video id="videoplayerads" class="video-js vjs-big-play-centered vjs-default-skin vjs-fluid" muted preload="auto" playsinline></video> ' +
      '<div class="companion-container"> <div id="companionAd"></div> </div> ' +
      '<div class="companion-container-mb"> <div id="companionAdMobile"> </div> </div>';

    if (isPlatformBrowser(this.platformId)) {
      // Subscribe to login state
      this.store.select(selectAuthLoginState).subscribe(state => {
        this.loginState = state;
      });
    }
  }

  ngAfterViewInit(): void {
    this.divVideoPlayerRef().nativeElement.innerHTML = this.videoContent;
    this.logService.info('AFTER VIEW INIT: ', this.adTagUrl);

    if (isPlatformBrowser(this.platformId)) {
      this.initializeInpageCmp();

      this.player.on('adserror', (response) => {
        this.logService.info('AdsError response: ', response);
        // Your code in response to the `adserror` event.
        switch (response.data.AdError.data.errorCode) {
          case 1009: //  vast empty
          case 402: // timeout
          case 400: // play error
            this.logService.info(
              'Case AdsError: ',
              response.data.AdError.data.errorCode + ' - ' + response.data.AdError.data.errorMessage
            );
            this.isShow.set(false);
            try {
              if (this.player && !this.player.isDisposed()) {
                this.player.dispose();
              }
            } catch (error) {
              this.logService.info('player is: ', this.player);
              this.logService.error('Error disposing video player', error);
            }
            this.changeDetectorRef.detectChanges();
            break;
          default:
            this.logService.info(
              'AdsError unknown Error: ',
              response.data.AdError.data.errorCode + ' - ' + response.data.AdError.data.errorMessage
            );
            break;
        }
      });

      this.player.on('ended', () => {
        this.logService.info('Ended video');
        this.closeInpageCmp();
      });

      this.player.on('event', (res) => {
        this.logService.info('adevent video: ', res);
      });
    }
  }

  ngOnDestroy(): void {
    try {
      if (this.player && !this.player.isDisposed()) {
        this.player.dispose();
      }
    } catch (error) {
      this.logService.info('player is: ', this.player);
      this.logService.error('Error disposing video player', error);
    }
  }

  closeInpageCmp() {
    this.isShow.set(false);
    this.changeDetectorRef.detectChanges();

    // Verifica che this.player e this.player.ima esistano prima di chiamare controller.reset()
    if (this.player) {
      // Prima resetta il player
      try {
        this.player.reset();
      } catch (error) {
        this.logService.error('Error resetting player', error);
      }

      // Poi verifica e resetta il controller ima se disponibile
      if (this.player.ima && this.player.ima.controller) {
        try {
          this.player.ima.controller.reset();
        } catch (error) {
          this.logService.error('Error resetting ima controller', error);
        }
      } else {
        this.logService.info('IMA controller not available for reset');
      }
    }
  }

  initializeInpageCmp() {
    this.isShow.set(true);
    this.changeDetectorRef.detectChanges();

    this.player = videojs(this.divVideoPlayerRef().nativeElement.querySelector('#videoplayerads'), {
      restoreEl: true,
      autoplay: true,
      sources: [
        {
          src: this.envService.contentPath + '/content/video/0094241-video-nero-05sec.mp4',
          type: 'video/mp4'
        }
      ]
    });

    if (this.adTagUrl) {
      this.player.ima({
        adTagUrl: this.adTagUrl
      });

      // Attendi che il plugin IMA sia completamente inizializzato
      this.player.ready(() => {
        if (!this.player.ima || !this.player.ima.controller) {
          this.logService.info('IMA plugin not fully initialized yet');
        }
      });
    }
  }

  private handleTargetBannerChange(target: TargetBanner) {
    if (!isPlatformBrowser(this.platformId)) return;

    const isUserLoggedIn = this.loginState === AuthLoginState.LOGGEDHARD ||
      this.loginState === AuthLoginState.LOGGEDSOFT;

    if (isUserLoggedIn) {
      this.setupCompanionSlots();
    }

    this.cat = String(target.idCategoria);
    this.pr = String(target.idProvincia);
    this.spec = String(target.idSpecializzazione);
    this.cont = String(target.idContenuto);
    this.tcont = String(target.idTipoContenuto);
    this.can = String(target.idCanale);
    this.tcan = String(target.idTipoCanale);

    this.adTagUrl =
      this.envService.adTagUrlCompanion +
      this.envService.adUnitCompanion +
      '&tfcd=0&npa=0&sz=640x480&ciu_szs=320x50,728x90&gdfp_req=1&output=vast&unviewed_position_start=1&env=vp&impl=s' +
      '&cust_params=cat%3D' +
      this.cat +
      '%26spec%3D' +
      this.spec +
      '%26cont%3D' +
      this.cont +
      '%26tcont%3D' +
      this.tcont +
      '%26can%3D' +
      this.can +
      '%26tcan%3D' +
      this.tcan;

    this.logService.info('VideoADS: adTagUrl updated', this.adTagUrl);

    // If player already exists, update it
    if (this.player && 'ima' in this.player) {
      this.reloadPlayer();
    }
  }

  private setupCompanionSlots() {
    const existingCompanionSlot = googletag.pubads().getSlots().find(slot =>
      slot.getAdUnitPath() === this.envService.adUnitCompanion
    );

    if (!existingCompanionSlot) {
      googletag.cmd.push(() => {
        if (document.getElementById(this.envService.divCompanion)) {
          googletag
            .defineSlot(
              this.envService.adUnitCompanion,
              [728, 90],
              this.envService.divCompanion
            )
            .defineSizeMapping(
              googletag
                .sizeMapping()
                .addSize(
                  [1024, 768],
                  [
                    [750, 200],
                    [728, 90]
                  ]
                )
                .addSize([980, 690], [728, 90])
                .addSize([640, 480], 'fluid')
                .addSize([0, 0], 'fluid') // All viewports < 640x480
                .build()
            )
            .addService(googletag.companionAds())
            .addService(googletag.pubads());

          googletag.cmd.push(() => {
            googletag.display(this.envService.divCompanion);
          });
        }

        if (document.getElementById(this.envService.divCompanionMobile)) {
          googletag
            .defineSlot(
              this.envService.adUnitCompanion,
              [320, 50],
              this.envService.divCompanionMobile
            )
            .defineSizeMapping(
              googletag
                .sizeMapping()
                .addSize(
                  [1024, 768],
                  [
                    [750, 200],
                    [728, 90],
                    [320, 50]
                  ]
                )
                .addSize([980, 690], [728, 90])
                .addSize([640, 480], 'fluid')
                .addSize([0, 0], 'fluid') // All viewports < 640x480
                .build()
            )
            .addService(googletag.companionAds())
            .addService(googletag.pubads());

          googletag.cmd.push(() => {
            googletag.display(this.envService.divCompanionMobile);
          });
        }

        googletag.cmd.push(() => {
          googletag.companionAds().setRefreshUnfilledSlots(true);
          googletag.pubads().enableVideoAds();
          googletag.enableServices();
        });
      });
    } else {
      existingCompanionSlot.addService(googletag.pubads());
      googletag.pubads().refresh();
    }
  }

  private reloadPlayer() {
    try {
      if (this.player && !this.player.isDisposed()) {
        this.player.dispose();
      }
    } catch (error) {
      this.logService.info('player is: ', this.player);
      this.logService.error('Error disposing video player', error);
    }

    this.divVideoPlayerRef().nativeElement.innerHTML = this.videoContent;
    this.initializeInpageCmp();
  }
}
